import React, { useEffect, useRef, useState } from "react";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const SEVERITY_TONE = { hard_block: "danger", required: "warn", advisory: "muted" };
const POLL_MS = 1500;

function ExtractButton({ sourceId, chunkCount, onExtracted }) {
  const [job, setJob] = useState(null);
  const [err, setErr] = useState(null);
  const pollRef = useRef(null);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const start = async () => {
    setErr(null); setJob({ id: null, status: "queued" });
    try {
      const r = await api.post(`/knowledge/sources/${sourceId}/extract`, {});
      const jobId = r.data.job_id;
      setJob({ id: jobId, status: "queued" });
      pollRef.current = setInterval(async () => {
        try {
          const j = await api.get(`/intelligence/jobs/${jobId}`);
          const doc = j.data.job;
          setJob(doc);
          if (doc.status === "succeeded" || doc.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (doc.status === "succeeded") onExtracted?.();
          }
        } catch (_) { /* keep polling */ }
      }, 1500);
    } catch (e) {
      setErr(formatApiError(e));
      setJob(null);
    }
  };

  const busy = job && job.status !== "succeeded" && job.status !== "failed";
  const result = job?.result || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
      <button type="button" className="bo-btn bo-btn-ghost"
        style={{ padding: "3px 8px", fontSize: 12 }}
        disabled={busy || !chunkCount}
        onClick={start}
        data-testid={`extract-btn-${sourceId}`}
        title={chunkCount ? `Extract intelligence from ${chunkCount} chunk(s)` : "No chunks — re-ingest first"}
      >
        {busy ? (job?.status || "starting…") : (job?.status === "succeeded" ? "Extracted ✓" : "Extract")}
      </button>
      {job?.status === "succeeded" && (
        <span style={{ fontSize: 11, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}
              data-testid={`extract-result-${sourceId}`}>
          {result.total_proposals || 0} proposals · ${(result.total_cost_usd || 0).toFixed(4)}
        </span>
      )}
      {err && <span style={{ fontSize: 11, color: "var(--bo-danger)" }}>{err}</span>}
    </div>
  );
}

function WebsiteIngestCard({ brandId, onIngested }) {
  const [url, setUrl] = useState("");
  const [maxPages, setMaxPages] = useState(5);
  const [maxDepth, setMaxDepth] = useState(1);
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState(null);          // {id, status, result}
  const [err, setErr] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startIngest = async (e) => {
    e.preventDefault();
    setErr(null); setStarting(true); setJob(null);
    try {
      const r = await api.post("/knowledge/ingest-website", {
        root_url: url.trim(),
        brand_id: brandId || null,
        max_pages: Number(maxPages),
        max_depth: Number(maxDepth),
      });
      const jobId = r.data.job_id;
      setJob({ id: jobId, status: "queued" });
      pollRef.current = setInterval(async () => {
        try {
          const j = await api.get(`/intelligence/jobs/${jobId}`);
          const doc = j.data.job;
          setJob(doc);
          if (doc.status === "succeeded" || doc.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (doc.status === "succeeded") onIngested?.();
          }
        } catch (_) { /* keep polling */ }
      }, POLL_MS);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setStarting(false);
    }
  };

  const result = job?.result || {};
  const stats = result.stats || {};

  return (
    <div className="bo-card" data-testid="vault-ingest-card">
      <div className="bo-eyebrow">Bounded website ingestion · Slice 5b</div>
      <h2 className="bo-card-title">Ingest a website</h2>
      <p className="bo-card-lede">
        SSRF-safe crawler. Deterministic, <strong>no LLM</strong> in the crawl itself — pages are
        fetched, chunked, and stored. Extraction runs later, only when you say so.
      </p>
      <form onSubmit={startIngest} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 8, alignItems: "end", marginTop: 8 }}>
        <label>
          <div className="bo-eyebrow">Root URL</div>
          <input className="bo-input" type="url" required placeholder="https://precipice-health.com/about"
            value={url} onChange={e => setUrl(e.target.value)} data-testid="vault-ingest-url" />
        </label>
        <label>
          <div className="bo-eyebrow">Max pages</div>
          <input className="bo-input" type="number" min={1} max={50}
            value={maxPages} onChange={e => setMaxPages(e.target.value)} data-testid="vault-ingest-maxpages" />
        </label>
        <label>
          <div className="bo-eyebrow">Max depth</div>
          <input className="bo-input" type="number" min={0} max={4}
            value={maxDepth} onChange={e => setMaxDepth(e.target.value)} data-testid="vault-ingest-maxdepth" />
        </label>
        <button type="submit" className="bo-btn bo-btn-primary" disabled={starting || !url.trim()}
          data-testid="vault-ingest-btn">
          {starting ? "Starting…" : "Ingest"}
        </button>
      </form>
      {err && <div className="bo-login-error" role="alert" style={{ marginTop: 10 }} data-testid="vault-ingest-error">{err}</div>}
      {job && (
        <div style={{ marginTop: 12, padding: 12, background: "var(--bo-panel-alt)", border: "1px solid var(--bo-line)", borderRadius: "var(--bo-radius-sm)" }} data-testid="vault-ingest-status">
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Pill tone={job.status === "failed" ? "danger" : (job.status === "succeeded" ? undefined : "muted")}>
              {job.status}
            </Pill>
            <span style={{ fontSize: 12, color: "var(--bo-muted)", fontFamily: "var(--bo-mono)" }}>
              job {job.id?.slice(0, 8)}…
            </span>
          </div>
          {job.status === "succeeded" && (
            <div style={{ marginTop: 8, fontSize: 13 }} data-testid="vault-ingest-result">
              <div>Pages fetched: <strong>{stats.pages_fetched || 0}</strong> · Chunks created: <strong>{result.total_chunks || 0}</strong></div>
              <div>Bytes: {(stats.total_bytes || 0).toLocaleString()} · Skipped: {stats.pages_skipped || 0} · Errored: {stats.pages_errored || 0}</div>
              <div>Same-origin links found: {stats.total_same_origin_links_found || 0} · Unique URLs seen: {stats.unique_urls_seen || 0}</div>
              {stats.pages_fetched === 1 && (stats.total_same_origin_links_found || 0) === 0 && (
                <div style={{ color: "var(--bo-muted)", fontSize: 12, marginTop: 4, fontStyle: "italic" }}>
                  Only 1 page fetched because no same-origin &lt;a href&gt; links were discovered.
                  Likely a client-side-routed SPA or a single-page site.
                </div>
              )}
              {(result.errors || []).slice(0, 3).map((e, i) => (
                <div key={i} style={{ color: "var(--bo-warn, #b57e28)", fontSize: 12 }}>· {e.url}: {e.error}</div>
              ))}
            </div>
          )}
          {job.status === "failed" && (
            <div style={{ marginTop: 8, color: "var(--bo-danger)", fontSize: 13 }}>
              {job.error?.message || "Ingestion failed."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function BrandVault() {
  const [state, setState] = useState({ loading: true, error: null, brand: null, voice: null, rules: [], sources: [] });

  const loadAll = async () => {
    try {
      const brandsRes = await api.get("/brands");
      const brand = brandsRes.data.brands?.[0];
      if (!brand) throw new Error("No brand configured for this organization.");
      const detailRes = await api.get(`/brands/${brand.id}`);
      const sourcesRes = await api.get("/knowledge/sources", { params: { brand_id: brand.id } });
      setState({
        loading: false, error: null,
        brand: detailRes.data.brand,
        voice: detailRes.data.voice,
        rules: detailRes.data.rules || [],
        sources: sourcesRes.data.sources || [],
      });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: formatApiError(e) }));
    }
  };

  useEffect(() => { loadAll(); }, []);

  const { brand, voice, rules, sources } = state;

  return (
    <Shell title="Brand Vault">
      <div className="bo-eyebrow">Foundation</div>
      <h1 className="bo-serif" style={{ fontSize: 34, lineHeight: 1.1, letterSpacing: "-0.02em", margin: "6px 0 22px" }} data-testid="vault-title">
        The Brand Vault
      </h1>

      <ErrorBanner error={state.error} />
      {state.loading && <LoadingCard label="Loading brand foundation…" />}

      {brand && (
        <>
          <div className="bo-card" data-testid="vault-brand-card">
            <div className="bo-eyebrow">Brand</div>
            <h2 className="bo-card-title" data-testid="vault-brand-name">{brand.name}</h2>
            <p className="bo-card-lede">{brand.mission}</p>
            {brand.promise && (
              <p style={{ fontStyle: "italic", color: "var(--bo-ink-soft)", marginBottom: 12 }}>
                Promise — <strong>{brand.promise}</strong>
              </p>
            )}
            {brand.values?.length > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="vault-values">
                {brand.values.map((v) => <Pill key={v}>{v}</Pill>)}
              </div>
            )}
          </div>

          {voice && (
            <div className="bo-card" data-testid="vault-voice-card">
              <div className="bo-eyebrow">Voice</div>
              <h2 className="bo-card-title">How Precipice sounds</h2>
              <p className="bo-card-lede">{voice.personality}</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
                <div>
                  <div className="bo-eyebrow" style={{ marginBottom: 6 }}>Tone</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {(voice.tone || []).map((t) => <Pill key={t}>{t}</Pill>)}
                  </div>
                </div>
                <div>
                  <div className="bo-eyebrow" style={{ marginBottom: 6 }}>Forbidden terms</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="vault-forbidden-terms">
                    {(voice.forbidden_terminology || []).map((t) => <Pill key={t} tone="danger">{t}</Pill>)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bo-card" data-testid="vault-rules-card">
            <div className="bo-eyebrow">Governance rules</div>
            <h2 className="bo-card-title">{rules.length} active brand rule{rules.length === 1 ? "" : "s"}</h2>
            <p className="bo-card-lede">
              These rules are enforced by the Brand Guardian on every generated asset (Slice 3+).
            </p>
            {rules.length === 0 ? <EmptyState title="No rules yet" description="Add rules to govern generated copy." /> : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }} data-testid="vault-rules-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--bo-line)" }}>
                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--bo-muted)", fontWeight: 600 }}>Severity</th>
                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--bo-muted)", fontWeight: 600 }}>Category</th>
                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--bo-muted)", fontWeight: 600 }}>Rule</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((r) => (
                    <tr key={r.id} style={{ borderBottom: "1px solid var(--bo-line)" }} data-testid={`rule-row-${r.category}`}>
                      <td style={{ padding: "10px" }}><Pill tone={SEVERITY_TONE[r.severity]}>{r.severity.replace("_", " ")}</Pill></td>
                      <td style={{ padding: "10px" }}>{r.category}</td>
                      <td style={{ padding: "10px", color: "var(--bo-ink-soft)" }}>{r.text}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <WebsiteIngestCard brandId={brand?.id} onIngested={loadAll} />

          <div className="bo-card" data-testid="vault-sources-card">
            <div className="bo-eyebrow">Knowledge sources</div>
            <h2 className="bo-card-title">{sources.length} source{sources.length === 1 ? "" : "s"} indexed</h2>
            <p className="bo-card-lede">
              Sources power evidence, retrieval, and provenance. Try the Retrieval Explorer to see them in action.
            </p>
            {sources.length === 0 ? <EmptyState title="No sources yet" description="Ingest documents to build your knowledge vault." /> : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }} data-testid="vault-sources-table">
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--bo-line)" }}>
                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--bo-muted)", fontWeight: 600 }}>Title</th>
                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--bo-muted)", fontWeight: 600 }}>Authority</th>
                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--bo-muted)", fontWeight: 600 }}>Lifecycle</th>
                    <th style={{ textAlign: "left", padding: "8px 10px", color: "var(--bo-muted)", fontWeight: 600 }}>Chunks</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", color: "var(--bo-muted)", fontWeight: 600 }}>Extract</th>
                  </tr>
                </thead>
                <tbody>
                  {sources.map((s) => (
                    <tr key={s.id} style={{ borderBottom: "1px solid var(--bo-line)" }} data-testid={`source-row-${s.authority}`}>
                      <td style={{ padding: "10px", fontWeight: 500 }}>{s.title}</td>
                      <td style={{ padding: "10px" }}><Pill tone={s.authority === "Canonical" || s.authority === "Approved" ? undefined : "muted"}>{s.authority}</Pill></td>
                      <td style={{ padding: "10px" }}>
                        <Pill tone={s.lifecycle === "Superseded" || s.lifecycle === "Retired" ? "warn" : undefined}>{s.lifecycle}</Pill>
                      </td>
                      <td style={{ padding: "10px", fontFamily: "var(--bo-mono)" }}>{s.chunk_count}</td>
                      <td style={{ padding: "10px", textAlign: "right" }}>
                        <ExtractButton sourceId={s.id} chunkCount={s.chunk_count} onExtracted={loadAll} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </Shell>
  );
}
